/**
 * Supported payment services configuration.
 *
 * Each service defines:
 * - name: Display name
 * - key: Unique identifier stored in DB (paymentService column)
 * - regions: Which regions this service is popular in
 * - handlePrefix: What to show before the handle (e.g. "@" for Venmo)
 * - placeholder: Input placeholder text
 * - urlTemplate: Deep link or web URL template. null = copy-only (no deep link).
 *   Placeholders: {handle}, {amount}, {note}
 * - payUrlTemplate: URL for paying someone (if different from requesting)
 * - supportsAmount: Whether the URL supports pre-filling an amount
 */

export interface PaymentServiceConfig {
  name: string;
  key: string;
  regions: string[];
  handlePrefix: string;
  placeholder: string;
  payUrlTemplate: string | null;
  chargeUrlTemplate: string | null;
  supportsAmount: boolean;
  // true = saved handles may be phone numbers (digits-only URL cleaning +
  // phone-style display formatting for legacy handles). Does NOT mean the
  // UI should offer phone entry — that's autoLinkPhone.
  usesPhoneNumber: boolean;
  // true = the "link this service" toggles may one-tap link the login phone
  // number as the handle. Deliberately OFF for Venmo: paying a phone number
  // shows the raw number in Venmo (no name / profile pic), so payers can't
  // tell who they're paying — Venmo is handle-only in every entry UI.
  autoLinkPhone?: boolean;
  icon?: string; // emoji for now
}

export const PAYMENT_SERVICES: PaymentServiceConfig[] = [
  // ~90M+ US users — most popular P2P app in US
  {
    name: 'Venmo',
    key: 'Venmo',
    regions: ['US'],
    handlePrefix: '@',
    placeholder: 'handle',
    // iOS: custom scheme (opens Venmo app via URL handler — note decorators may be
    //      stripped by the app, but recipient + amount land cleanly).
    // Android + web: universal link (App Links route to Venmo app on Android; on web,
    //      lands on venmo.com profile). Selected at runtime in buildPaymentUrl.
    // The /{handle} path isn't in Venmo's iOS apple-app-site-association, so universal
    // links don't open the iOS app — stick with the custom scheme there.
    payUrlTemplate: 'venmo://paycharge?txn=pay&recipients={handle}&amount={amount}&note={note}',
    chargeUrlTemplate: 'venmo://paycharge?txn=charge&recipients={handle}&amount={amount}&note={note}',
    supportsAmount: true,
    // Legacy handles may still be phone numbers (from the old auto-link),
    // so URL cleaning + phone display stay on — but no new phone entry.
    usesPhoneNumber: true,
    autoLinkPhone: false,
    icon: '💸',
  },
  // ~120M+ US users — built into most US bank apps
  {
    name: 'Zelle',
    key: 'Zelle',
    regions: ['US'],
    handlePrefix: '',
    placeholder: 'Phone # or email',
    payUrlTemplate: null,
    chargeUrlTemplate: null,
    supportsAmount: false,
    usesPhoneNumber: true,
    autoLinkPhone: true,
    icon: '⚡',
  },
  // ~430M+ active accounts globally
  {
    name: 'PayPal',
    key: 'PayPal',
    regions: ['US', 'UK', 'EU', 'ES', 'FR', 'DE', 'IT'],
    handlePrefix: '',
    placeholder: 'paypal.me name',
    payUrlTemplate: 'https://paypal.me/{handle}/{amount}',
    chargeUrlTemplate: null,
    supportsAmount: true,
    usesPhoneNumber: false,
    icon: '🅿️',
  },
  // ~55M+ US/UK users
  {
    name: 'Cash App',
    key: 'Cash App',
    regions: ['US', 'UK'],
    handlePrefix: '$',
    placeholder: 'cashtag',
    payUrlTemplate: 'https://cash.app/{handle}/{amount}',
    chargeUrlTemplate: null,
    supportsAmount: true,
    usesPhoneNumber: false,
    icon: '💵',
  },
  // ~40M+ users across EU/UK/US
  {
    name: 'Revolut',
    key: 'Revolut',
    regions: ['UK', 'EU', 'US'],
    handlePrefix: '',
    placeholder: 'revolut.me name',
    payUrlTemplate: 'https://revolut.me/{handle}',
    chargeUrlTemplate: null,
    supportsAmount: false,
    usesPhoneNumber: false,
    icon: '🔄',
  },
  // ~27M+ users in Spain
  {
    name: 'Bizum',
    key: 'Bizum',
    regions: ['ES'],
    handlePrefix: '',
    placeholder: 'phone number',
    payUrlTemplate: null,
    chargeUrlTemplate: null,
    supportsAmount: false,
    usesPhoneNumber: true,
    autoLinkPhone: true,
    icon: '📱',
  },
  // ~16M+ international users. Copy-only: the old wise.com/pay/{handle}/{amount}
  // template 404s (verified 2026-07-25 — Wise's real pay links are
  // wise.com/pay/me/<wisetag>, no amount segment), and handles here may be
  // EMAILS, which no URL form can express. The copy popup (amount + handle)
  // is the only shape that works for both handle kinds.
  {
    name: 'Wise',
    key: 'Wise',
    regions: ['US', 'UK', 'EU'],
    handlePrefix: '',
    placeholder: 'username or email',
    payUrlTemplate: null,
    chargeUrlTemplate: null,
    supportsAmount: false,
    usesPhoneNumber: false,
    icon: '🌍',
  },
  // ~9M+ UK users
  {
    name: 'Monzo',
    key: 'Monzo',
    regions: ['UK'],
    handlePrefix: '',
    placeholder: 'monzo.me name',
    payUrlTemplate: 'https://monzo.me/{handle}',
    chargeUrlTemplate: null,
    supportsAmount: false,
    usesPhoneNumber: false,
    icon: '🏦',
  },
  // ~7M+ users in France
  {
    name: 'Lydia',
    key: 'Lydia',
    regions: ['FR'],
    handlePrefix: '',
    placeholder: 'username',
    payUrlTemplate: 'https://lydia-app.com/collect/request/{handle}',
    chargeUrlTemplate: null,
    supportsAmount: false,
    usesPhoneNumber: false,
    icon: '🇫🇷',
  },
];

/**
 * Get a payment service config by key.
 */
export function getPaymentService(key: string): PaymentServiceConfig | undefined {
  return PAYMENT_SERVICES.find(s => s.key === key);
}

/**
 * Get payment services for a given region.
 */
export function getPaymentServicesForRegion(region: string): PaymentServiceConfig[] {
  return PAYMENT_SERVICES.filter(s => s.regions.includes(region));
}

/**
 * Detect iOS at runtime. Capacitor's getPlatform() is the source of truth in the app;
 * fall back to user agent for mobile-web users hitting our site outside the app.
 */
function isIOSRuntime(): boolean {
  if (typeof window === 'undefined') return false;
  const cap = (window as any).Capacitor?.getPlatform?.();
  if (cap === 'ios') return true;
  if (cap === 'android') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent || '');
}

/** True inside the native app (Capacitor iOS/Android) — custom URL schemes
 *  launch as external intents there, so venmo:// is the reliable deep link. */
function isNativeRuntime(): boolean {
  if (typeof window === 'undefined') return false;
  const cap = (window as any).Capacitor?.getPlatform?.();
  return cap === 'ios' || cap === 'android';
}

// Android Venmo recipient strategy. Venmo's Android composer resolves
// `recipients` through people-search: hyphens read as spaces ("alex-brennan"
// pre-addresses the handle AND any contact named "Alex"), and a username
// that collides with a person's NAME fans out the same way — "felicity"
// opened the composer addressed to BOTH the person "Felicity" and
// @felicity (seen on device 2026-07-30, single-token lowercase handle). A
// sent payment would SPLIT between the chips. NO username spelling avoids
// it (bare/lowercase/exact-case/@-prefix/web-link/leading-comma all fan
// out — full matrix in VENMO_ANDROID_HANDOFF.md); a PHONE recipient pins
// exactly one account (also what Splitwise's Android successor apps ship).
// iOS pins usernames fine and is untouched. Venmo has broken this surface
// twice (2024, mid-2026) — flip to 'username' to kill the workaround
// instantly.
export const ANDROID_VENMO_HYPHEN_STRATEGY: 'phone' | 'username' = 'phone';

/** True when this pay/request should try the counterparty-phone substitution:
 *  ANY non-empty Venmo recipient on native Android — originally hyphenated
 *  usernames only, widened after the name-collision fan-out above. Call
 *  sites use this to decide whether to resolve the (friendship-gated) phone
 *  before building; the username stays the fallback when no phone resolves. */
export function needsAndroidVenmoPhone(serviceKey: string, handle: string): boolean {
  return ANDROID_VENMO_HYPHEN_STRATEGY === 'phone'
    && serviceKey === 'Venmo'
    && isNativeRuntime() && !isIOSRuntime()
    && !!(handle || '').trim();
}

/**
 * Build a payment URL for a given service.
 * Returns null if the service doesn't support deep links (copy-only).
 */
export function buildPaymentUrl(
  serviceKey: string,
  handle: string,
  amount: number,
  note: string,
  isPayment: boolean = true, // true = pay, false = charge/request
  opts?: {
    /** Counterparty's phone (friendship-gated resolve by the call site) —
     *  substituted for hyphenated Venmo usernames on Android. See
     *  needsAndroidVenmoPhone / ANDROID_VENMO_HYPHEN_STRATEGY. */
    counterpartyPhone?: string | null;
    /** ISO 4217 code of `amount`. Every templated service here denominates in
     *  USD (Venmo, Cash App, PayPal.me read a bare number as dollars), so a
     *  NON-USD amount is STRIPPED from the URL — the link opens with recipient
     *  + note and no prefilled figure — instead of moving the wrong money
     *  (¥15,000 prefilled as $15,000). Callers should clipboard-copy the
     *  amount in that case: see deepLinkCarriesAmount. Absent = USD. */
    amountCurrency?: string | null;
  },
): string | null {
  const service = getPaymentService(serviceKey);
  if (!service) return null;

  let template = isPayment ? service.payUrlTemplate : service.chargeUrlTemplate;
  if (!template) return null;

  // Venmo: on WEB (mobile web + desktop), use the universal link — it lands on
  // venmo.com with the txn prefilled. NATIVE apps (iOS *and* Android) keep the
  // venmo:// custom scheme: Capacitor launches it as an external intent and the
  // Venmo app opens its composer with recipient/amount/note prefilled. The
  // https://venmo.com/?txn=… App Link on native Android opened the Venmo app
  // but IGNORED the query params (blank composer — no amount/user/note), which
  // broke Request there. Recipient goes in the `recipients` query param, not
  // the path: Venmo routes /{handle} as "show profile" rather than "pay user".
  if (service.key === 'Venmo' && !isIOSRuntime() && !isNativeRuntime()) {
    template = isPayment
      ? 'https://venmo.com/?txn=pay&recipients={handle}&amount={amount}&note={note}'
      : 'https://venmo.com/?txn=charge&recipients={handle}&amount={amount}&note={note}';
  }

  // Non-USD amount → drop the amount token (query and path forms) so a foreign
  // figure is never prefilled into a dollar field. MUST run AFTER any template
  // reassignment above (the Venmo web override) — stripping first and then
  // swapping in an amount-bearing template would silently reinstate it.
  if (opts?.amountCurrency && opts.amountCurrency.toUpperCase() !== 'USD') {
    template = template
      .replace('amount={amount}&', '')
      .replace('&amount={amount}', '')
      .replace('amount={amount}', '')
      .replace('/{amount}', '');
  }

  // Clean up handle
  let cleanHandle = handle.trim();
  if (service.handlePrefix && cleanHandle.startsWith(service.handlePrefix)) {
    cleanHandle = cleanHandle.slice(service.handlePrefix.length);
  }
  if (cleanHandle.startsWith('@')) {
    cleanHandle = cleanHandle.slice(1);
  }
  cleanHandle = cleanHandle.trim();

  // For phone-number-based services, clean to digits only. Run this BEFORE the
  // single-token guard below so a "555 123 4567" entry survives as its digits
  // rather than tripping the internal-whitespace check.
  if (service.usesPhoneNumber) {
    const digitsOnly = cleanHandle.replace(/\D/g, '');
    if (digitsOnly.length >= 9 && /^\d+$/.test(digitsOnly)) {
      cleanHandle = digitsOnly;
    }
  }

  // Single-token guard. A real handle for any supported service is ONE token —
  // no username / cashtag / paypal.me name / phone-digit string legitimately
  // contains internal whitespace or a comma. Two things can violate that:
  //   1. An Android soft keyboard appends a TRAILING space after autocomplete
  //      ("johnny " for "johnny") — harmless, and already removed by trim above.
  //   2. A display NAME gets co-stored in the handle field ("Kimberly kimberly",
  //      "E Johnson") — data contamination. Venmo's ANDROID app then SPLITS the
  //      `recipients` value on the whitespace/comma and opens the composer
  //      addressed to TWO people (the name AND the handle).
  // Recover the real handle: keep the LAST whitespace/comma-delimited token. The
  // name is always PREPENDED ("<name> <handle>"), so the handle is last — this
  // yields ONE correct, pre-addressed recipient (no split). (Blanking it — the
  // earlier fix — killed the split but left "no recipient", which read as broken;
  // collapsing the whitespace mashed it into a bogus handle.) Phone digits were
  // already extracted above, so a numeric handle never reaches this branch.
  if (/[\s,]/.test(cleanHandle)) {
    const parts = cleanHandle.split(/[\s,]+/).filter(Boolean);
    cleanHandle = parts[parts.length - 1] || '';
  }

  // Venmo usernames are case-insensitive and canonically lowercase. A stored
  // mixed-case handle ("Domingo") can make Venmo's Android app treat the
  // `recipients` value as a NAME search rather than an exact @username, which
  // pre-populates the composer with same-named contacts. Lowercasing pins it to
  // the exact username. (Only Venmo — other services' handles are left as-is.)
  if (service.key === 'Venmo') {
    cleanHandle = cleanHandle.toLowerCase();
  }

  // Venmo on Android: swap in the counterparty's phone when the call site
  // resolved one — the only recipient form that pins exactly one account
  // there (see needsAndroidVenmoPhone above; applies to every username, not
  // just hyphenated, since the name-collision fan-out). Obvious
  // placeholders (555 area code — the test-family accounts) fall back to the
  // username so a fake number is never addressed.
  if (needsAndroidVenmoPhone(service.key, cleanHandle) && opts?.counterpartyPhone) {
    const digits = opts.counterpartyPhone.replace(/\D/g, '');
    if (digits.length >= 10 && !digits.replace(/^1/, '').startsWith('555')) {
      cleanHandle = digits;
    }
  }

  // No recipient (the logged-out Tabster "Request" fallback when the
  // selected person has no linked Venmo to pre-address): drop the
  // `recipients` token entirely so the link opens Venmo's composer with
  // just the amount + note and a blank recipient line — the payer adds
  // the person in-app. `venmo.com/?txn=charge&amount=…&note=…` is the
  // documented "attach the user in-app" form; emitting an empty
  // `recipients=` param instead risks Venmo treating it as an invalid
  // username. Only Venmo's templates carry `recipients={handle}`.
  if (!cleanHandle) {
    template = template
      .replace('recipients={handle}&', '')
      .replace('&recipients={handle}', '')
      .replace('recipients={handle}', '');
  }

  const encodedHandle = encodeURIComponent(cleanHandle);
  const encodedAmount = encodeURIComponent(amount.toFixed(2));
  const encodedNote = encodeURIComponent(note);

  return template
    .replace('{handle}', encodedHandle)
    .replace('{amount}', encodedAmount)
    .replace('{note}', encodedNote);
}

/**
 * Whether a deep link built for this service will actually CARRY the amount:
 * the service supports URL amounts AND the amount is in USD (the currency all
 * templated services denominate in — buildPaymentUrl strips non-USD amounts).
 * When false, call sites should clipboard-copy the amount so the user can
 * paste it into the payment app.
 */
export function deepLinkCarriesAmount(serviceKey: string, amountCurrency?: string | null): boolean {
  const service = getPaymentService(serviceKey);
  if (!service?.supportsAmount) return false;
  return !amountCurrency || amountCurrency.toUpperCase() === 'USD';
}

/**
 * Reduce a stored/typed payment handle to a single clean recipient token — the
 * WRITE-side twin of the whitespace guard inside buildPaymentUrl (keep the two in
 * sync). A display NAME co-stored with the handle ("Kimberly kimberly") is the
 * only way internal whitespace/commas get into a non-phone handle, and Venmo's
 * ANDROID app SPLITS the `recipients` value on that whitespace into TWO recipients
 * (the name AND the handle) — so neither a store nor an emit should ever carry
 * such a value. Guarding the two DB writers (writeDocPaymentInfo /
 * writeUserPaymentInfo) with this keeps contamination from being persisted in the
 * first place, so the buildPaymentUrl guard is only a last-resort net.
 *
 * - Phone-number services (Zelle etc.): a real handle can legitimately contain
 *   spaces ("555 123 4567"), so it's left as-is here (buildPaymentUrl reduces it
 *   to digits at emit time).
 * - Everything else: trim; if internal whitespace/comma remains it's contaminated
 *   — BLANK it. We can't know which token is the real handle, and a wrong single
 *   recipient is worse than none. A clean handle passes through untouched.
 */
export function sanitizePaymentHandle(serviceKey: string, raw: string | null | undefined): string {
  const service = getPaymentService(serviceKey);
  const h = (raw ?? '').trim();
  if (!h) return '';
  // Mirror buildPaymentUrl's ORDER exactly. Phone-capable services (incl. Venmo,
  // whose usesPhoneNumber stays on for legacy phone handles): if the value looks
  // like a phone number, reduce to digits (this also strips the spaces in
  // "555 123 4567"). A non-numeric value (a Venmo username like "kimberly", or a
  // contaminated "Kimberly kimberly") does NOT match and falls through — so the
  // whitespace guard below still fires. Without this fall-through, Venmo — the
  // one service with the Android split — would be skipped entirely.
  if (service?.usesPhoneNumber) {
    const digits = h.replace(/\D/g, '');
    if (digits.length >= 9 && /^\d+$/.test(digits)) return digits;
  }
  // A display name co-stored with the handle is the only source of internal
  // whitespace/comma left in a username handle (Venmo-Android splits `recipients`
  // on it into two people). Recover the real handle — the LAST token, since names
  // are prepended — rather than blanking it, so the stored value stays usable. A
  // clean handle passes through untouched. (Mirrors the buildPaymentUrl guard.)
  if (/[\s,]/.test(h)) {
    const parts = h.split(/[\s,]+/).filter(Boolean);
    return parts[parts.length - 1] || '';
  }
  return h;
}

/**
 * Whether a service supports opening a URL (vs copy-only).
 */
export function serviceHasDeepLink(serviceKey: string): boolean {
  const service = getPaymentService(serviceKey);
  return !!(service?.payUrlTemplate);
}

/**
 * Launch a payment-app deep link (venmo://, https://paypal.me/…) without
 * unloading the current page, so the user lands back where they tapped Pay
 * when they return from the payment app. Synthesized target="_blank" anchor
 * click: Android's WebView hands the venmo:// (etc.) scheme to the OS with
 * the full query intact, and http(s) links leave the WebView instead of
 * navigating it. Setting window.location.href instead navigated the WebVIEW
 * on Android — prefill dropped (Venmo opened to its homepage) and the
 * return-to-app state was lost, so the "mark paid" prompt never fired; on
 * web it unloads the SPA (page state gone on return). iOS works either way;
 * the anchor click works everywhere. Falls back to location.href only if the
 * anchor path throws.
 */
export function launchPaymentDeepLink(url: string): void {
  if (!url || typeof document === 'undefined') return;
  try {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    // Hidden so it doesn't visually flash.
    a.style.cssText = 'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { try { document.body.removeChild(a); } catch {} }, 0);
  } catch {
    try { window.location.href = url; } catch {}
  }
}
